import React, { useCallback, useEffect, useRef, useState } from "react";
import ProjectResourceGrid, { ProjectResource } from "../project/projectResourceGrid";
import { ServiceReportTimesInGrid } from "./serviceReportTimesInGrid";
import { RSplitter, RSplitterDirection } from "../core/components/rSplitter";
import { HTTPService, JsonResultClass } from "../core/util/httpManager";
import { NotificationHelper } from "../core/util/notificationHelper";
import { Helper } from "../core/util/helper";
import { DropDownColumn } from "../core/components/editableGrid/editableGrid";

const ServiceReportOtherTimes = (props) => {
    const [resources, setResources] = useState(null);
    const [timeTypes, setTimeTypes] = useState(null);
    const filter = useRef(null);

    useEffect(() => {
        loadData();
    }, [props.entity.Projnr]);

    const loadData = async () => {
        const resources = await HTTPService.get<ProjectResource[]>("ProjectResource/GetResources?projNr=" + encodeURIComponent(props.entity.Projnr));

        const ogrNrs = [...new Set(resources.map(x => x.OgrNr).concat(props.entity.RessOgrNr))];
        const url = "ServiceReportLookUp/GetTimeTypes?" + Helper.createQueryStringParameters("ogrNrs", ogrNrs);
        const timeTypes = await HTTPService.get<any[]>(url);

        setResources(resources.filter(x => x.RessNr != props.entity.Ressnr));
        setTimeTypes(timeTypes);
    };

    const getTimeTypes = (ogrNr) => {
        var timeTypesInternal = timeTypes.filter(x => x["OgrNr"] == ogrNr);
        if (timeTypesInternal.length == 0)
            timeTypesInternal = timeTypes.filter(x => x["OgrNr"] == 0);
        if (props.entity.RecordRight.IsRueckModusAdded)
            timeTypesInternal = timeTypesInternal.filter(x => x.AllowAfterSign);
        return timeTypesInternal;
    };

    const isDeleted = useCallback((ressNr) => {
        return resources.some(x => x.RessNr == ressNr && x.Deleted);
    }, [resources]);

    const originText = useCallback((ressNr) => {
        const resource = resources.find(x => x.RessNr == ressNr);
        if(!resource)
            return "";

        return resource.IsIntern ? Helper.getTranslation("Ressource.Ressource") : Helper.getTranslation("Field.Projekt.ProjectResource");
    }, [resources]);

    const rowReadOnly = useCallback((dataItem) => {
        let readonlyRueckModus = !dataItem.ErfasstNachSignatur && props.entity.RecordRight.IsRueckModusAdded;
        let readonly = Helper.hasValue(dataItem.Readonly) && dataItem.Readonly == 1;
        return readonlyRueckModus || readonly || dataItem.IsMoved || isDeleted(dataItem.RessNr);
    }, [isDeleted]);

    const onDataBound = useCallback((e) => {
        e.sender.items().map((idx, row) => {
            let dataItem = e.sender.dataItem(row);
            if (dataItem.IsMoved) {
                $(row).find("td[data-field=MovedToBelegNr]").css("background-color", "rgb(85, 139, 47)").css("color", "white");
            };
        });
    }, []);

    const onLayoutReady = useCallback((columns, schema) => {
        var columnTimeTypeIsGuid: DropDownColumn = columns.find(x => x.field == "TimeTypeId");
        columnTimeTypeIsGuid.refreshChoices = (dataItem) => {
            let resource = resources.find(x => x.IsIntern && x.RessNr == dataItem.RessNr);
            let timeTypes = getTimeTypes(resource?.OgrNr ?? props.entity.RessOgrNr);
            return timeTypes.map(x => {
                return {
                    Value: x.TimeTypeId,
                    Text: x.Text
                };
            });
        };

        var columnRessNr = columns.find(x => x.field == "RessNr");
        columnRessNr.refreshChoices = () => {
            return resources.filter(x => !x.Deleted).map(x => {
                return {
                    Value: x.RessNr,
                    Text: x.Name
                };
            });
        }
        columnRessNr["filterable"] = {
            ui: function (element) {
                element.kendoDropDownList({
                    dataSource: resources,
                    dataTextField: "Name",
                    dataValueField: "RessNr"
                });
            },
            operators: {
                string: {
                    eq: kendo.ui.FilterMenu.prototype.options.operators.string.eq
                }
            },
            cell: {
                showOperators: false,
                operator: "eq"
            },
            extra: false
        }
    }, [resources, timeTypes]);

    const handleItemChanged = useCallback((fieldName, dataItem) => {
        const clearDriverAndDistanceIfRequired = (timeType: number, dataItem: any) => {
            if (timeType != 1 && timeType != 3 && timeType != 9) {
                dataItem.set("IsDriver", null);
                dataItem.set("Traveldistance", null);
            }
        }

        switch (fieldName) {
            case "TimeTypeId":
                let timeType = timeTypes.find(x => x.TimeTypeId == dataItem.TimeTypeId);
                dataItem.set("Zeittyp", timeType.Id);
                clearDriverAndDistanceIfRequired(timeType.Id, dataItem)
                if (timeType.IsStandard) {
                    dataItem.set("Timetypeisguid", null);
                } else {
                    dataItem.set("Timetypeisguid", timeType.IsGuid);
                }
                break;
            case "RessNr":
                let resource = resources.find(x => x.RessNr == dataItem.RessNr);
                let timeTypesRess = getTimeTypes(resource?.OgrNr ?? props.entity.RessOgrNr);
                if (!timeTypesRess.some(x => x.TimeTypeId == dataItem.TimeTypeId)) {
                    dataItem.set("TimeTypeId", null);
                    dataItem.set("Zeittyp", null);
                    dataItem.set("Timetypeisguid", null);
                }
                break;
        }
    }, [resources, timeTypes]);

    const createNew = useCallback((newEntity) => {
        newEntity["IsMoved"] = false;
        newEntity["MovedToBelegNr"] = 0;
        newEntity["RessNr"] = null;
        if (filter.current) {
            const ressNr = filter.current.filters.find(x => x.field == "RessNr")?.value;
            if (!isDeleted(ressNr))
                newEntity["RessNr"] = ressNr;
        }
    }, [isDeleted]);

    if (!resources || !timeTypes)
        return null;

    props.dataSources["otherTechnicians"] = resources;
    props.dataSources["timeTypes"] = timeTypes;

    return <div className="flex-container other-times">
        <ServiceReportTimesInGrid
            entity={props.entity}
            data={props.entity.OtherTimes.Data}
            gridOptions={props.otherTimesInGridOptions}
            dataSources={props.dataSources}
            filterable={true}
            pagable={true}
            onDataBound={onDataBound}
            onLayoutReady={onLayoutReady}
            rowReadOnly={rowReadOnly}
            createNewEntity={createNew}
            onFilter={(e) => filter.current = e.filter}
            handleItemChanged={handleItemChanged}
            ref={(ref) => props.inGridOtherTimesRef(ref)}
            {...{ isDeleted: isDeleted, originText: originText }}
            id="otherTechniciansSrTimes"
        />
    </div>
}

export default ServiceReportOtherTimes;